import { useSession } from '@acp/feature/auth';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { UaEventOptions } from 'react-ga4/types/ga4';
import { useLocation } from 'react-router-dom';

export type GoogleAnalyticsEvent = {
    options: UaEventOptions | string;
    eventParams?: unknown;
};

export function useGoogleAnalytics(): void {
    const location = useLocation();

    useEffect(() => {
        if (import.meta.env.VITE_PUBLIC_GA_ENABLE === '1') {
            ReactGA.send({
                hitType: 'pageview',
                page: location.pathname + location.search,
            });
        }
    }, [location]);
}

export function setupGoogleAnalytics(): void {
    if (
        import.meta.env.VITE_PUBLIC_GA_ENABLE === '1'
        && import.meta.env.VITE_PUBLIC_GA_TAG !== undefined
    ) {
        const tag = import.meta.env.VITE_PUBLIC_GA_TAG;

        ReactGA.initialize(tag);
    }
}

export function addGoogleAnalyticsEvent({ options, eventParams }: GoogleAnalyticsEvent) {
    if (import.meta.env.VITE_PUBLIC_GA_ENABLE === '1') {
        ReactGA.event(options, eventParams);
    }
}

// This sets user-specific properties for Google Analytics
// https://developers.google.com/analytics/devguides/collection/ga4/user-properties
export function useSetGoogleAnalyticsUserProperties() {
    const session = useSession();

    useEffect(() => {
        if (import.meta.env.VITE_PUBLIC_GA_ENABLE !== '1' || session === undefined) return;

        ReactGA.gtag('set', 'user_properties', {
            compliance_agency_state: session.complianceAgencyState,
            is_impersonating: session.isImpersonating ? 'Yes' : 'No',
        });
    }, [session]);
}
